import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import Svg from "../svg/Svg";
import "./Button.scss";
import { Icon } from "../../icons/Icon";
import { translationService } from "../../services/translation/TranslationService";

export interface ButtonProps {
  id?: string;
  type?: "button" | "submit" | "reset";
  className?: string;
  style?: any;
  disabled?: boolean;
  onClick?: (event?: any) => void;
  textKey?: string;
  labelKey?: string;
  icon?: Icon;
  component?: JSX.Element;
  isHidden?: boolean;
  tooltipKey?: string;
  children?: JSX.Element;
}

export default observer(
  React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(props: ButtonProps, ref) {
    const {
      id,
      type,
      className,
      style,
      disabled,
      onClick,
      textKey,
      labelKey,
      icon,
      component,
      isHidden,
      tooltipKey,
      children,
      ...remainingProps
    } = props;

    const buttonIcon = useMemo(
      () =>
        icon && (
          <Svg
            svg={icon.svg}
            width={icon.width || 50}
            height={icon.height || 50}
            className={icon.className}
          />
        ),
      [icon]
    );

    const simpleButton = (
      <button
        {...remainingProps}
        ref={ref}
        id={id}
        type={type || "button"}
        style={style || {}}
        className={`button ${className}`}
        disabled={disabled}
        onClick={onClick}
        title={tooltipKey ? translationService.translate(tooltipKey) : ""}
      >
        {textKey ? translationService.translate(textKey) : null}
        {component ? component : buttonIcon}
        {icon && icon.textKey && !labelKey && (
          <span className={icon.textClass}>{translationService.translate(icon.textKey)}</span>
        )}
        {children}
      </button>
    );

    const labeledButton = labelKey && (
      <div className="btn-wrapper">
        {simpleButton}
        <label>{translationService.translate(labelKey)}</label>
      </div>
    );

    if (isHidden) return null;
    return labeledButton || simpleButton;
  })
);
